import { NgClass, NgForOf, NgIf } from '@angular/common';
import {
  Component,
  effect,
  input,
  InputSignal,
  OnInit,
  output,
  OutputEmitterRef,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatStep, MatStepContent, MatStepLabel, MatStepper, MatStepperIcon } from '@angular/material/stepper';
import { FormioForm } from '@formio/angular';
import { TranslateModule } from '@ngx-translate/core';
import { get, isNil } from 'lodash';
import { filter } from 'rxjs/operators';
import { EDialogWidth } from '../../../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../../../core/validators/trimmed-required.validator';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../../../shared/components/datatable/datatable.interface';
import { ErrorMessagesComponent } from '../../../../../shared/components/error-messages/error-messages.component';
import { IBaseDatatableState } from '../../../../../state/base-datatable-store.service';
import { ELoadStatus } from '../../../../../state/state.interface';
import {
  DateUtilitiesService,
  HOUR_MINUTE_SECONDS_STRING_REGEX,
} from '../../../../../utilities/date-utilities.service';
import { FormUtilitiesService } from '../../../../../utilities/form-utilities.service';
import { HttpUtilitiesService } from '../../../../../utilities/http-utilities.service';
import { ESnackbar } from '../../../../../utilities/snackbar.service';
import { BaseCrudPageComponent } from '../../../../app/base-crud-page.component';
import { IWorkInstructionEditor } from '../../work-instructions.interface';
import { SaveAsTemplateDialogComponent } from './save-as-template-dialog/save-as-template-dialog.component';
import {
  IStepForm,
  IWorkInstructionStep,
  TAddWorkInstructionStep,
  TFormControlWorkInstructionStep,
  TFormWorkInstructionStepComponent,
} from './work-instruction-steps.interface';
import { WorkInstructionStepsStore } from './work-instruction-steps.store';

@Component({
  imports: [
    ErrorMessagesComponent,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    NgIf,
    ReactiveFormsModule,
    TranslateModule,
    MatStepper,
    MatStep,
    MatStepLabel,
    NgForOf,
    MatStepContent,
    MatStepperIcon,
    NgClass,
    MatHint,
  ],
  providers: [WorkInstructionStepsStore, HttpUtilitiesService],
  selector: 'app-work-instruction-steps',
  standalone: true,
  styleUrl: './work-instruction-steps.component.scss',
  templateUrl: './work-instruction-steps.component.html',
})
export class WorkInstructionStepsComponent
  extends BaseCrudPageComponent<IWorkInstructionStep, IBaseDatatableState<IWorkInstructionStep>>
  implements OnInit
{
  @ViewChild('addEditFormDialog') addEditFormDialog!: TemplateRef<HTMLElement>;
  @ViewChild('stepper') stepper: MatStepper | undefined;

  public workInstruction: InputSignal<IWorkInstructionEditor> = input.required();
  public readonly editedFormOfSelectedItem = input<FormioForm>();
  public selectedItem = output<IWorkInstructionStep>();
  public stepForms: OutputEmitterRef<IStepForm[]> = output<IStepForm[]>();

  public datatableConfigurations: IDatatableConfiguration<IWorkInstructionStep> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.single,
    editScope: ETableCrudScope.single,
  };

  public readonly datatableColumns: IDatatableColumn<IWorkInstructionStep>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
  ];

  private dialogRef: MatDialogRef<HTMLElement> | undefined;
  private editedForms: IStepForm[] = [];
  private selectedStep: IWorkInstructionStep | undefined;

  constructor(
    public readonly store: WorkInstructionStepsStore,
    public readonly formService: FormUtilitiesService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfWorkInstructionStep',
      multiContext: 'field.workInstructionSteps',
      nameProperty: 'name',
      singleContext: 'field.workInstructionStep',
    });

    effect(() => {
      const [selectedItem] = this.selectedRows();
      this.selectedStep = selectedItem;
    });

    effect(() => {
      const form: FormioForm | undefined = this.editedFormOfSelectedItem();

      if (this.selectedStep && form) {
        this.setStepForm(this.selectedStep.id, form);
      }
    });
  }

  public setStepForm(stepId: number, form: FormioForm): void {
    const otherSteps: IStepForm[] = this.editedForms.filter((item: IStepForm) => item.stepId !== stepId);
    otherSteps.push({ form, workInstructionId: this.workInstruction().id, stepId });
    this.editedForms = otherSteps;

    this.stepForms.emit(this.editedForms);
  }

  public readonly addEditForm: FormGroup<TFormControlWorkInstructionStep> = new FormGroup({
    name: new FormControl('', [
      Validators.required,
      TrimmedRequiredValidator(),
      Validators.minLength(1),
      Validators.maxLength(100),
    ]),
    order: new FormControl(0, [Validators.min(0), Validators.max(1000)]),
    timeToComplete: new FormControl('', [Validators.pattern(HOUR_MINUTE_SECONDS_STRING_REGEX)]),
  });

  public ngOnInit(): void {
    this.getItems();
    this.stepForms.emit([]);

    this.subscriptions.push(
      this.store
        .select((state: IBaseDatatableState<IWorkInstructionStep>) => state.singleCrudLoading)
        .pipe(filter((value: ELoadStatus) => value === ELoadStatus.success))
        .subscribe(() => {
          this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
          this.getItems();
          this.dialogRef?.close();
        }),
      this.store
        .select((state: IBaseDatatableState<IWorkInstructionStep>) => state.dataLoading)
        .pipe(filter((value: ELoadStatus) => value === ELoadStatus.success))
        .subscribe(() => {
          const [selectedItem] = this.selectedRows();
          const selectedStepId: number = selectedItem ? selectedItem.id : get(this.store.data$(), '0.id');

          if (selectedStepId) {
            this.onChangeStep(selectedStepId);
          } else {
            this.initializeAdd();
          }
        }),
    );
  }

  public initializeAdd(): void {
    this.setupForAdd();
    this.addEditForm.enable();

    this.addEditForm.reset({
      name: '',
      order: this.store.data$().length + 1,
      timeToComplete: '',
    });

    this.dialogRef = this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public openSaveAsTemplateModal(): void {
    const item = this.selectedRow()!;
    const stepForm: FormioForm | undefined =
      this.editedForms.find((form: IStepForm) => form.stepId === item.id)?.form || item.form;

    if (!stepForm) {
      this.snackbarService.open(this.translate.instant('system.message.noChanges'));

      return;
    }

    this.matDialog.open(SaveAsTemplateDialogComponent, {
      data: {
        templateToSave: stepForm,
      },
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public initializeEdit(): void {
    if (!this.selectedRows().length) {
      return;
    }

    this.addEditForm.enable();
    this.setupForEdit();

    if (this.isBulkEdit()) {
      this.addEditForm.disable();
      this.addEditForm.reset();
    } else {
      const [item] = this.selectedRows();

      this.addEditForm.reset({
        name: item.name,
        order: item.order,
        timeToComplete: isNil(item.targetDuration)
          ? null
          : DateUtilitiesService.formatSecondsToTimer(item.targetDuration),
      });
    }

    this.dialogRef = this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public onFormDialogSubmit(): void {
    if (!this.addEditForm.valid) {
      return;
    }

    if (!this.isEditDialog()) {
      this.store.createOne(this.formatOnePayload(this.addEditForm.value as TFormWorkInstructionStepComponent));
    } else {
      const [item] = this.selectedRows();
      this.store.updateOne({
        dto: this.formatOnePayload(this.addEditForm.value as TFormWorkInstructionStepComponent),
        id: item.id,
      });
    }
  }

  private getItems(): void {
    this.store.loadWorkInstructionSteps({
      fields: ['name', 'order', 'form', 'targetDuration'],
      filters: [{ field: 'guideId', ids: [this.workInstruction().id] }],
      limit: 1000,
      page: 1,
      sort: [{ active: 'order', direction: 'asc' }],
    });
  }

  private formatOnePayload(step: TFormWorkInstructionStepComponent): TAddWorkInstructionStep {
    return {
      guideId: this.workInstruction().id,
      name: FormUtilitiesService.trimIfString(step.name),
      order: step.order,
      targetDuration: DateUtilitiesService.timeStringToSeconds(step.timeToComplete),
    };
  }

  public onChangeStep(id?: number): void {
    const stepId: number = id ?? Number(this.stepper?.selected?.label);

    if (!stepId) {
      return;
    }

    this.selectedDatatableItems.set([stepId]);
    const [item] = this.selectedRows();
    const editedForm: IStepForm | undefined = this.editedForms.find((form: IStepForm) => form.stepId === stepId);

    item.form = editedForm ? editedForm.form : item.form;
    this.selectedItem.emit(item);
  }


}
